import React, { Component, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { compose } from 'redux';
import {
    TableCell, TableHead, TableSortLabel, TableRow, Checkbox,
    Tooltip,
    Box,
    Zoom,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import PersistentStorage from '../../services/storage';
import WithCopyButton from './TableCells/WithCopyButton';
import { COLORS, isMac } from '../../utils';

export const SmallerTableCell = compose(Wrapped => ({
    isVisible,
    ...props
}) => {
    const [isWordExpanded, setIsWordExpanded] = useState(false);

    const overflow = isVisible ? 'visible' : 'hidden';
    const style = isWordExpanded ?
        { ...props.style, whiteSpace: 'normal', maxWidth: 140, wordWrap: 'break-word' } : { ...props.style, overflow };

    const onDoubleClick = () => !isWordExpanded && setIsWordExpanded(true);
    const onMouseDown = e => (e.detail > 1 || e.ctrlKey) && e.preventDefault();

    return (
        <Wrapped
            onDoubleClick={onDoubleClick}
            {...props}
            style={style}
            onMouseDown={onMouseDown}
        />
    );
}, withStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    head: {},
    body: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 80,
    }
})))(TableCell);

export const SmallerTableCellWithCopyButton = compose(Wrapped => ({
    type,
    title,
    titleTooltip,
    isTooltip,
    mark,
    changeFont,
    ...props
}) => (
    <Wrapped
        id={props.id}
        tabIndex={0}
        title={title}
    >
        {isTooltip && (
            <Tooltip
                title={titleTooltip}
                arrow
                TransitionComponent={Zoom}
                placement="top-start"
            >
                <Box
                    sx={{ position: 'absolute',
                        zIndex: 1,
                        top: 0,
                        left: 0,
                        cursor: 'help',
                        fontWeight: 'bold',
                        color: COLORS.secondary.main }}
                >
                    {mark}
                </Box>
            </Tooltip>
        )}
        <WithCopyButton title={title}>
            {props.children}
        </WithCopyButton>
    </Wrapped>
), withStyles(() => ({
    body: {
        fontFamily: 'Helvetica, Arial, sans-serif',
        position: 'relative',
        '&:focus': {
            outline: 'unset',
        },
    },
})))(SmallerTableCell);

export const ExpandedContentTableCell = compose(Wrapped => ({
    type,
    title,
    onCtrlClick,
    isExpanded,
    setIsExpanded,
    expandedContentType,
    setExpandedContentType,
    isSameContentType,
    isCopy = true,
    ...props
}) => {

    const onClick = useCallback(e => {
        const triggerKey = isMac() ? e.metaKey : e.ctrlKey;
        if (triggerKey && onCtrlClick) {
            onCtrlClick(e);
        } else if (title && setIsExpanded) {
            setExpandedContentType && !isSameContentType && setExpandedContentType(expandedContentType);
            isSameContentType ? setIsExpanded(!isExpanded) : setIsExpanded(true);
        }
    }, [isSameContentType, isExpanded, expandedContentType, setExpandedContentType, title, onCtrlClick]);

    const onDoubleClick = useCallback(e => e.preventDefault(), []);

    return (
        <Wrapped
            id={props.id}
            tabIndex={0}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            title={title}
        >
            { isCopy ? (
                <WithCopyButton title={title}>
                    {props.children}
                </WithCopyButton>
            ) : (
                <>
                    {props.children}
                </>
            )}
        </Wrapped>
    );
}, withStyles(theme => ({
    body: {
        position: 'relative',
        color: theme.palette.accent.main,
        textShadow: `0 0 0.5px ${theme.palette.primary.main}`,
        '&:hover': {
            color: theme.palette.secondary.main,
            cursor: 'pointer',
            textDecoration: 'underline',
            textShadow: `0 0 0`
        },
        '&:focus': {
            outline: 'unset',
            textDecoration: 'underline',
        },
    },
})))(SmallerTableCell);

const HeadTableCell = withStyles(theme => ({
    head: {
        height: 'inherit',
        color: theme.palette.common.white,
        fontSize: 14,
        letterSpacing: '0.03rem',
    }
}))(SmallerTableCell);

const HeadTableSortLabel = withStyles(theme => ({
    root: {
        color: theme.palette.accent.main,
        textShadow: `0 0 0.5px ${theme.palette.primary.main}`,
        fontWeight: 'normal',
        '&:focus': {
            color: theme.palette.primary.main,
        },
        '&:hover': {
            color: theme.palette.secondary.main
        }
    },
    active: {
        fontWeight: 'bold',
    }
}))(TableSortLabel);

const HeadTableSortLabelNoSort = withStyles(() => ({
    root: {
        cursor: 'not-allowed',
    },
}))(HeadTableSortLabel);

const StorageIdPrefix = 'rowWidth';
const config = { attributes: true };
const DEBOUNCE_TIME = 200;

class EnhancedTableHead extends Component {
    constructor(props) {
        super(props);

        this.observers = [];
        this.observerCallback = debounce(this.observerCallback, DEBOUNCE_TIME);
    }

    observerCallback = mutationsList => {
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes') {
                PersistentStorage.setItem(mutation.target.id, mutation.target.clientWidth);
            }
        }
    }

    componentDidMount() {
        this.props.headerRows.forEach(item => {
            const resizeDOMElement = document.getElementById(`${StorageIdPrefix}_${item.id}`);

            if (!resizeDOMElement) return;

            const observer = new MutationObserver(this.observerCallback);
            observer.observe(resizeDOMElement, config);
            this.observers.push(observer);
        });
    }

    componentWillUnmount() {
        this.observers.forEach(observer => {
            observer.disconnect();
        });
        this.observers = [];
    }

    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const {
            order, orderBy, headerRows, onSelectAllClick, numSelected, rowCount, actionGroup, classes, t
        } = this.props;

        return (
            <TableHead>
                <TableRow>
                    {actionGroup &&
                    (
                        <TableCell padding="checkbox">
                            <Checkbox
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{
                                    'aria-label': 'Description'
                                }}
                            />
                        </TableCell>
                    )}

                    {headerRows.map(row => {
                        let header = null;

                        if (row.showColumn === false) return null;

                        if (row.disableSorting) {
                            header = (
                                <HeadTableSortLabelNoSort hideSortIcon>
                                    {t(row.label)}
                                    {`${row.required ? '*' : ''}`}
                                </HeadTableSortLabelNoSort>
                            );
                        } else {
                            header = (
                                <HeadTableSortLabel
                                    active={orderBy === row.id}
                                    direction={order}
                                    onClick={this.createSortHandler(row.id)}
                                >
                                    {t(row.label)}
                                    {`${row.required ? '*' : ''}`}
                                </HeadTableSortLabel>
                            );
                        }

                        const storageRowId = `${StorageIdPrefix}_${row.id}`;
                        const storageRowStyles = { width: Number(PersistentStorage.getItem(storageRowId)) || 'inherit' };

                        return (
                            <HeadTableCell
                                key={row.id}
                                align={row.numeric ? 'right' : 'left'}
                                padding={row.disablePadding ? 'none' : 'normal'}
                                sortDirection={orderBy === row.id ? order : false}
                            >
                                <div
                                    className={classes.resize}
                                    id={storageRowId}
                                    style={storageRowStyles}
                                >
                                    {header}
                                </div>
                            </HeadTableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func,
    order: PropTypes.string,
    orderBy: PropTypes.string.isRequired,
    numSelected: PropTypes.number,
    rowCount: PropTypes.number,
    actionGroup: PropTypes.bool
};

EnhancedTableHead.defaultProps = {
    onRequestSort: () => { },
    order: 'asc',
};

export default withStyles(() => ({
    resize: {
        display: 'flex',
        height: '100%',
        position: 'relative',
        resize: 'horizontal',
        overflow: 'hidden',
        minWidth: 'min-content',
    },
}))(EnhancedTableHead);
